import { useCallback, useEffect, useRef, useState } from "react";
import { io, type Socket } from "socket.io-client";
import {
  EMPTY_EIGHTBALL_SNAPSHOT,
  type EightballSeat,
  type EightballSnapshot,
} from "@ziolo/shared";

export function useEightballSocket(enabled: boolean, room: string | null) {
  const socketRef = useRef<Socket | null>(null);
  const [state, setState] = useState<EightballSnapshot>(EMPTY_EIGHTBALL_SNAPSHOT);
  const [mySeat, setMySeat] = useState<EightballSeat | -1>(-1);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !room) return;

    const socket = io(`${window.location.origin}/8ball`, {
      path: "/socket.io/",
      withCredentials: true,
      transports: ["polling", "websocket"],
      forceNew: true,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      setConnected(true);
      setError(null);
      socket.emit("join_room", { room });
    });
    socket.on("disconnect", () => setConnected(false));
    socket.on("connect_error", (err: Error) => {
      setConnected(false);
      setError(
        err.message === "UNAUTHORIZED"
          ? "Sesja wygasła. Zaloguj się ponownie."
          : "Nie udało się połączyć ze stołem. Odśwież stronę.",
      );
    });
    socket.on("state", (next: EightballSnapshot) => setState(next));
    socket.on("snapshot", (next: EightballSnapshot) => setState(next));
    socket.on("your_seat", (seat: number) => {
      setMySeat(seat === 0 || seat === 1 ? seat : -1);
    });
    socket.on("notice", (payload: { text?: string }) => {
      if (typeof payload?.text === "string") setNotice(payload.text);
    });
    socket.on("foul", (payload: { message?: string }) => {
      if (typeof payload?.message === "string") setNotice(payload.message);
    });
    socket.on("placement_error", (payload: { message?: string }) => {
      if (typeof payload?.message === "string") setNotice(payload.message);
    });
    socket.on("game_end", (payload: { message?: string }) => {
      if (typeof payload?.message === "string") setNotice(payload.message);
    });
    socket.on("game_restart", (payload: { message?: string }) => {
      if (typeof payload?.message === "string") setNotice(payload.message);
    });
    socket.on("rematch_status", (payload: { message?: string }) => {
      if (typeof payload?.message === "string") setNotice(payload.message);
    });
    socket.on("rematch_declined", (payload: { message?: string }) => {
      if (typeof payload?.message === "string") setNotice(payload.message);
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
      setMySeat(-1);
      setState(EMPTY_EIGHTBALL_SNAPSHOT);
    };
  }, [enabled, room]);

  useEffect(() => {
    if (!notice) return;
    const id = window.setTimeout(() => setNotice(null), 5000);
    return () => window.clearTimeout(id);
  }, [notice]);

  const strike = useCallback((angleDeg: number, power: number) => {
    socketRef.current?.emit("strike", { angleDeg, power });
  }, []);

  const placeCue = useCallback((x: number, y: number) => {
    socketRef.current?.emit("place_cue_ball", { x, y });
  }, []);

  const requestRematch = useCallback(() => {
    socketRef.current?.emit("request_rematch");
  }, []);

  const declineRematch = useCallback(() => {
    socketRef.current?.emit("decline_rematch");
  }, []);

  return {
    state,
    mySeat,
    connected,
    error,
    notice,
    strike,
    placeCue,
    requestRematch,
    declineRematch,
  };
}
